/**
 * clientOnlyMessageFields — the ChatMessage slots that must never reach a
 * persist boundary (2026-09-08, PR-2).
 *
 *   attachments      live `File[]` — not JSON-roundtrippable (the 2026-05-11
 *                    thumbnails regression); `attachmentsSerialized` is the record
 *   deliveryStatus   render-only annotation derived from the delivery ledger
 *   deliveryFailure  render-only reason text for a failed send
 *
 * Applied by BOTH boundaries — `sanitizeMessageForPersist` (localStorage /
 * the thread store) and `toMessageDoc` (Firestore) — so a status can never be
 * frozen into a stored message and read back as truth on another device.
 * `sharedMessageId` is NOT here: it is the durable join key and must persist.
 */
export const CLIENT_ONLY_MESSAGE_FIELDS = ['attachments', 'deliveryStatus', 'deliveryFailure'] as const;

export type ClientOnlyMessageField = (typeof CLIENT_ONLY_MESSAGE_FIELDS)[number];

/**
 * A STORED FILE'S BASE64 COPY STAYS IN THE SESSION (2026-09-28, Malaika's
 * freezes: the browser's storage bin filled, and two of her server transcripts
 * still carried 614k and 137k characters of base64 file copies).
 *
 * An attached picture or document carries its bytes as a `dataUrl` until it is
 * uploaded; after that the record also carries where the file lives. Once it
 * can be fetched AGAIN from there — a storage path or an ingest path, from which
 * a fresh link is minted when the signed one expires — the base64 copy is
 * dropped at both persist boundaries: it is kept in memory for this session and
 * never saved. A record with only a signed link (it expires in 7 days) or no
 * locator at all keeps its copy: it may be the only one there is.
 */
function hasRefetchableLocator(att: Record<string, unknown>): boolean {
  return (typeof att.storagePath === 'string' && att.storagePath.length > 0)
    || (typeof att.gcsPath === 'string' && att.gcsPath.length > 0);
}

/** The list without redundant base64 copies; the SAME reference when none had to go. */
export function withoutStoredFileCopies<T>(list: T): T {
  if (!Array.isArray(list)) return list;
  let changed = false;
  const out = list.map((raw) => {
    if (!raw || typeof raw !== 'object') return raw;
    const att = raw as Record<string, unknown>;
    if (att.dataUrl === undefined || !hasRefetchableLocator(att)) return raw;
    changed = true;
    const { dataUrl: _dropped, ...rest } = att;
    void _dropped;
    return rest;
  });
  return (changed ? out : list) as T;
}

/**
 * Returns the SAME reference when nothing had to be stripped (callers depend on
 * identity for React diffing), else a shallow copy without the client-only slots
 * and without a stored file's base64 copy.
 */
export function stripClientOnlyMessageFields<T extends object>(message: T): T {
  if (!message || typeof message !== 'object') return message;
  const rec = message as Record<string, unknown>;
  let dirty = false;
  for (const f of CLIENT_ONLY_MESSAGE_FIELDS) {
    if (rec[f] !== undefined) { dirty = true; break; }
  }
  const files = withoutStoredFileCopies(rec.attachmentsSerialized);
  if (files !== rec.attachmentsSerialized) dirty = true;
  if (!dirty) return message;
  const copy: Record<string, unknown> = { ...rec };
  for (const f of CLIENT_ONLY_MESSAGE_FIELDS) delete copy[f];
  if (files !== rec.attachmentsSerialized) copy.attachmentsSerialized = files;
  return copy as T;
}
